import React, {
    useState,
    useEffect,
    useContext,
    useMemo
} from "react";
import {
    Stack,
    Pivot,
    Modal,
    Dropdown,
    TextField,
    PivotItem,
    TooltipHost,
    PrimaryButton,
    ThemeProvider,
    IDropdownOption,
    DirectionalHint,
} from "@fluentui/react";
import {
    IGroup,
    IColumn,
    DetailsRow,
    DetailsList,
    DetailsHeader,
    IDetailsRowProps,
    IDetailsHeaderProps,
    CollapseAllVisibility,
    DetailsListLayoutMode
} from "@fluentui/react/lib/DetailsList";
import { TbFilterStar } from "react-icons/tb";
import styles from "./AnalyticsConvoList.module.css";

import {
    gChatlogItem,
    gsynclog,
    gSyncLogItem,
    InteractionFeedbackItem,
    gselectedgroup,
    gloggedinuser,
    gactivecomp
} from "../../interfaces";

import TableSyncLog from "../TableSyncLog/TableSyncLog";
import CollectedDataTable from "../CollectedDataTable/CollectedDataTable";
import { getHeaderG, qGetChatLog, qGetSyncLog } from "../../api/qpi";
import ChartSyncLogBar, { ChartFilterState } from "../ChartSyncLogBar/ChartSyncLogBar";
import { GlobalContext } from "../../GlobalContext";
import ChartAnalyticsBar, { ChatUserFilterState } from "../ChartAnalyticsBar/ChartAnalyticsBar";

import {
    LuThumbsDown,
    LuThumbsUp
} from "react-icons/lu";
import {
    downloadCSV,
    keyToDateUtc,
    safeJsonParse,
    formatDuration,
    getSingleFeedback,
    getLastSegmentLog,
    formatDateConvoList,
    formatDate30DaysConvoList
} from "../../util_glob";
import {
    libPivotStyles,
    detailsListTheme,
    stackStylesAnalytics,
    controlStylesAnalyticsConvoList,
    textFieldStylesAnalyticsConvoList,
    dropdownFileSrcStylesAnalyticsUsers,
    dropdownAnalyticsPeriod
} from "../../styles_glob";

import { PiFileCsv } from "react-icons/pi";

import {
    BiExpandVertical,
    BiCollapseVertical
} from "react-icons/bi";
import {
    MdExpandLess,
    MdExpandMore,
    MdOutlineVerticalAlignBottom
} from "react-icons/md";
import { HiOutlineDocumentDownload } from "react-icons/hi";
import { IoMdCloseCircleOutline } from "react-icons/io";

import { assistantCodes, reportingPeriodsDropdownOptions } from "./../../lsts";
import { formatDateTimeClientTimeZone } from "../detailedList/detailedMdls";


interface IUserListProps {
    selectedGroup: gselectedgroup;
    loggedInUser: gloggedinuser;
    aggC: number;
    activeComp: gactivecomp;
}


const AnalyticsConvoList: React.FC<IUserListProps> = ({ selectedGroup, loggedInUser, aggC, activeComp }) => {
    const [endDate, setEndDate]     = useState(formatDateConvoList(new Date().toISOString()));
    const [userMail, setUserMail]   = useState<string | number | undefined>("");
    const [startDate, setStartDate] = useState(formatDate30DaysConvoList(new Date().toISOString()));
    const [isItemDetailsOpen, setIsItemDetailsOpen] = useState<boolean>(false);
    const [activePeriod, setActivePeriod]   = useState<string | null>(null);
    const [chartFilter, setChartFilter]     = useState<ChartFilterState | null>(null);

    const [chosenItem, setChosenItem] = useState<gChatlogItem>();
    const [spaceChats, setSpaceChats] = useState<gChatlogItem[]>([]);
    const [fltrdChats, setFltrdChats] = useState<gChatlogItem[]>([]);
    const [spaceSyncs, setSpaceSyncs] = useState<gSyncLogItem[]>([]);
    const [collapsedGroups, setCollapsedGroups] = useState<{ [key: string]: boolean }>({});

    const [spaceChatsLoaded, setSpaceChatsLoaded] = useState(false);
    const [spaceSyncsLoaded, setSpaceSyncsLoaded] = useState(false);

    const [selectedKey, setSelectedKey] = useState(selectedGroup?.assistanttype != assistantCodes.ipa ? "ChatAnalytics" : "SyncLogAnalytics");
    const [isAllCollapsed, setIsAllCollapsed] = useState<boolean>(false);

    const [focusedConvoGroupKey, setFocusedConvoGroupKey] = useState<string | null>(null);

    const handleChartFilterChange = (filterState: ChartFilterState) => { setChartFilter(filterState) };
    const [chatUserFilter, setChatUserFilter] = useState<ChatUserFilterState | null>(null);
    const handleChatUserFilterChange = (filterState: ChatUserFilterState) => { setChatUserFilter(filterState) };

    useEffect(() => {
        getChatLog(aggC, selectedGroup, loggedInUser, activeComp).then(setSpaceChats);
        getSyncLog(aggC, selectedGroup, loggedInUser, activeComp).then(setSpaceSyncs);
    }, [aggC, selectedGroup, loggedInUser, activeComp]);

    useEffect(() => {
        spaceChats?.map(ch => { ch.usermail = filterOutSysM(ch.usermail) })
        setFltrdChats(
            spaceChats.filter(ch => {
                const date = ch.datestamp;
                return date >= new Date(startDate) && date <= new Date(endDate) && (!userMail || ch.usermail === userMail);
            })
        );
    }, [spaceChats, endDate, startDate, userMail]);

    const getChatLog = async (aggC: number, selgroup: gselectedgroup, loggeduser: gloggedinuser, activeComp: gactivecomp) => {
        let spaceChatLog = await qGetChatLog(aggC, selgroup, loggeduser, activeComp);
        setSpaceChats(spaceChatLog);
        setSpaceChatsLoaded(true);
        return spaceChatLog;
    }

    const getSyncLog = async (aggC: number, selgroup: gselectedgroup, loggeduser: gloggedinuser, activeComp: gactivecomp) : Promise<gSyncLogItem[]> => {
        let spaceSyncLog = await qGetSyncLog(aggC, selgroup, loggeduser, activeComp);
        setSpaceSyncs(spaceSyncLog);
        setSpaceSyncsLoaded(true);
        return spaceSyncLog;
    };

    const setPredefinedPeriod = (period: string) => {
        const now   = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        let start: Date;
        let end: Date;

        switch (period) {
            case 'thisWeek': {
                const dayOfWeek = today.getDay();
                const monday    = new Date(today);
                monday.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek));
                const sunday    = new Date(monday);
                sunday.setDate(monday.getDate() + 7);
                start = monday;
                end   = sunday;
                break;
            }
            case 'lastWeek': {
                const dayOfWeek  = today.getDay();
                const lastMonday = new Date(today);
                lastMonday.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek) - 7);
                const lastSunday = new Date(lastMonday);
                lastSunday.setDate(lastMonday.getDate() + 7);
                start = lastMonday;
                end   = lastSunday;
                break;
            }
            case 'last7Days': {
                start = new Date(today);
                start.setDate(today.getDate() - 6);
                end = today;
                break;
            }
            case 'currentMonth': {
                start = new Date(now.getFullYear(), now.getMonth(), 0);
                end   = new Date(now.getFullYear(), now.getMonth() + 1, 0);
                break;
            }
            case 'lastMonth': {
                start = new Date(now.getFullYear(), now.getMonth() - 1, 0);
                end   = new Date(now.getFullYear(), now.getMonth(), 0);
                break;
            }
            case 'last30Days': {
                start = new Date(today);
                start.setDate(today.getDate() - 30);
                end   = today;
                break;
            }
            default: return;
        }

        setStartDate(formatDateConvoList(start.toISOString()));
        setEndDate(formatDateConvoList(end.toISOString()));
        setActivePeriod(period);
    };


    // add filtered sync log based on current filter
    const fltrdSyncs = spaceSyncs.filter(sync => {
        const date = new Date(sync.stamp);
        return (
            date >= new Date(startDate) && date <= new Date(endDate) &&
            (!userMail || spaceChats.some(
                ch => ch.usermail === userMail &&
                ((ch.key ?? 0).toString())?.startsWith(date.toISOString().slice(0, 8).replace(/-/g, ''))
            ))
        );
    });

    // apply chart legend filter for table display
    const tableSyncs = useMemo(() => {
        return chartFilter?.isMultiOperator && chartFilter.visibleSids.size > 0
            ? fltrdSyncs.filter(sync => chartFilter.visibleSids.has(sync.sid))
            : fltrdSyncs;
    }, [chartFilter, fltrdSyncs]);

    // apply chat user filter for cards and table display
    const filteredChatsForDisplay = useMemo(() => {
        return chatUserFilter?.isMultiUser && chatUserFilter.visibleUsers.size > 0
            ? fltrdChats.filter(chat => chatUserFilter.visibleUsers.has(chat.usermail))
            : fltrdChats;
    }, [chatUserFilter, fltrdChats]);

    const columns: IColumn[] = [
        { key: "column1", name: "Date", fieldName: "key", minWidth: 50, maxWidth: 120, targetWidthProportion: 0.2, isResizable: true },
        {
            key: "column11", name: "",
            onRender: (item: gChatlogItem) => {
                {
                    return item.positive == 1 ? (
                        <LuThumbsUp color="#4bbc8e" size="17" />
                    ) : item.positive == 0 ? (
                        <TooltipHost content={item.feedback} directionalHint={DirectionalHint.topCenter}>
                            <LuThumbsDown color="#ffb279" size="17" />
                        </TooltipHost>
                    ) : ('');
                }
            },
            fieldName: "positive",
            minWidth: 20,
            maxWidth: 20,
            targetWidthProportion: 0.2,
            isResizable: false
        },
        {
            key: "column22",
            name: "❤️",
            ariaLabel: "Feedback",

            onRender: (item: gChatlogItem) => {return item.feedback},
            fieldName: "comment",
            minWidth: 20,
            maxWidth: 20,
            targetWidthProportion: 0.2,
            isResizable: true
        },
        ...(selectedGroup.assistanttype != assistantCodes.cca ?
            [{ key: "column2", name: "User", fieldName: "usermail", minWidth: 50, maxWidth: 80 , targetWidthProportion: 0.2, isResizable: true }] : []
        ),
        { key: "column3", name: "Query"   , fieldName: "question", minWidth: 50, maxWidth: 250, targetWidthProportion: 0.5, isResizable: true },
        { key: "column4", name: "Response", fieldName: "response", minWidth: 50, maxWidth: 550, targetWidthProportion: 0.9, isResizable: true },
        { key: "column5", name: "Assistant ID", fieldName: "groupid", minWidth: 50, maxWidth: 210, targetWidthProportion: 0.2, isResizable: true },
    ];

    const dropdownOptions: IDropdownOption[] = [
        { key: '', text: 'All' },
        ...spaceChats
            .reduce<string[]>((acc, item) => {
                if (!acc.includes(item.usermail)) acc.push(item.usermail);
                return acc;
            }, [])
            .map(mail => ({ key: mail, text: mail }))
    ];

    const qGetChatAudio = async (hwv: string) => {
        const params = new URLSearchParams({
            groupid: selectedGroup.companyid,
            hwv: hwv,
            cid: selectedGroup.companyid
        });
        const response = await fetch("/kgnf_get_wav" + `?${params.toString()}`, {
            headers: getHeaderG(loggedInUser)
        });
        if (!response.ok) throw new Error("Download failed");

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);

        // create temp <a> and click it to trigger download
        const a = document.createElement("a");
        a.href = url;
        a.download = getLastSegmentLog(hwv); // optional: set file name
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
    };



    const formatText = (content: string) => {
        const output = [];
        const lines = content.split("\\n");

        let listItems: any = [];
        lines.forEach((line, index) => {
            // handle headers
            if (line.startsWith("### ")) {
                // if there are accumulated list items, add them as a list before resetting
                if (listItems.length > 0) {
                    output.push(<ul key={`ul-${index}`}>{listItems}</ul>);
                    listItems = [];
                }
                output.push(<h3 key={index}>{line.substring(4)}</h3>);
            }
            // handle list items
            else if (line.match(/^\d+\. /)) {
                listItems.push(<li key={index}>{line.substring(line.indexOf(" ") + 1)}</li>);
            }
            // handle bold text within lines
            else if (line.includes("**")) {
                const parts = line.split("**").map((part, i) => (i % 2 === 1 ? <strong key={i}>{part}</strong> : part));
                output.push(<p key={index}>{parts}</p>);
            }
            // add explicit line breaks for empty lines
            else if (line.trim() === "") {
                output.push(<br key={index} />);
            }
            // normal paragraph text
            else {
                output.push(<p key={index}>{line}</p>);
            }
        });

        // if there are remaining list items at the end, add them as a list
        if (listItems.length > 0) {
            output.push(<ul key="final-ul">{listItems}</ul>);
        }

        return output;
    };


    const filterOutSysM = (mail: any) => {
        if (mail && mail == "ibkr@abv.bg") return "sys@kgnfy.com"
        return mail
    }

    const groupedChats = React.useMemo(() => {
        const chats = filteredChatsForDisplay
            .map(item => ({
                ...item,
                key: item.datestamp.toLocaleString(),
                ts: item.datestamp instanceof Date ? item.datestamp.getTime() : new Date(item.datestamp).getTime(),
                usermail: filterOutSysM(item.usermail)
            }))
            .sort((a, b) => {
                if (a.ts !== b.ts) return b.ts - a.ts;
                return a.key.localeCompare(b.key);
            });
        const groups: IGroup[] = [];
        let lastConvoId: string | number | null = null;
        let startIdx = 0;
        let interactionFeedback: InteractionFeedbackItem[] = [];
        chats.forEach((item, idx) => {
            interactionFeedback = [...interactionFeedback, ...getSingleFeedback(item)];
            if (item.convo_id !== lastConvoId) {
                if (lastConvoId !== null) {
                    groups[groups.length - 1].count = idx - startIdx;
                    startIdx = idx;
                    groups[groups.length - 1].data.feedback             = interactionFeedback;
                    groups[groups.length - 1].data.numericFeedbackSum   = interactionFeedback.reduce((sum, feedback) => sum + (feedback.numericFeedback || 0), 0);
                    groups[groups.length - 1].data.durEnd               = idx > 0 ? chats[idx - 1].key : null;
                    groups[groups.length - 1].data.duration             = formatDuration(
                        Math.round((new Date(groups[groups.length - 1].data.durEnd).getTime() - new Date(groups[groups.length - 1].data.durStart).getTime()) / 1000)
                    );
                }

                groups.push({
                    key: item.datestamp.toLocaleString(),
                    name: `${item.datestamp.toLocaleString()}`,
                    startIndex: idx,
                    count: 0,
                    isCollapsed: true,
                    data: {
                        collecteddata: item.collecteddata || {},
                        feedback: [],
                        email: item.usermail?.startsWith("ibkr") ? "no email" : item.usermail || "Unknown",
                        numericFeedbackSum: 0,
                        durStart: item.key,
                        durEnd: item.key,
                        duration: 0,
                        audio_logs: item.audio_logs || [],
                        groupname: item.groupname || ""
                    }
                });
                lastConvoId = item.convo_id;
                interactionFeedback = [];
            }
        });
        if (groups.length > 0) groups[groups.length - 1].count = chats.length - startIdx;

        return { chats, groups };
    }, [filteredChatsForDisplay]);

    // when a convo filter is active, show only that group in the table
    const displayChats = useMemo(() => {
        if (!focusedConvoGroupKey) return groupedChats;
        const group = groupedChats.groups.find(g => g.key === focusedConvoGroupKey);
        if (!group) return groupedChats;
        const chats = groupedChats.chats.slice(group.startIndex, group.startIndex + group.count);
        const filteredGroup = { ...group, startIndex: 0 };
        return { chats, groups: [filteredGroup] };
    }, [groupedChats, focusedConvoGroupKey]);

    const onRenderGroupHeader = (props: any): JSX.Element | null => {
        if (!props) return null;
        const { group } = props;
        const isCollapsed = collapsedGroups[group.key] || false;
        return (
            <div className={styles.groupHeader} onClick={() => { setCollapsedGroups(prev => ({ ...prev, [group.key]: !prev[group.key] })) }}>
                <span style={{ marginRight: 8 }}>{isCollapsed ? <MdExpandLess /> : <MdExpandMore />}</span>
                {group.name} ⇒ <b>{group?.data?.email || ''}</b> ⇛ <i>{group?.data?.groupname || ''}</i> ({group.count} interactions)
            </div>
        );
    };

    // custom group body renderer for animation
    const onRenderGroup = (props: any): JSX.Element | null => {
        if (!props) return null;
        const { group } = props;
        const isCollapsed = collapsedGroups[group.key] || false;
        return <div className={styles.groupBody + (isCollapsed ? " " + styles.groupBodyCollapsed : "")}>{props.onRenderGroupInner(props)}</div>;
    };

    const downloadAudio = (audioLog: string) => {
        qGetChatAudio(audioLog);
    };


    const onToggleCollapseAll = (isAllCollapsedNew: boolean): void => {
        if (isAllCollapsedNew === isAllCollapsed) return; // no change
        setIsAllCollapsed(isAllCollapsedNew);

        const newState: { [key: string]: boolean } = {};
        groupedChats.groups.forEach(g => { newState[g.key] = isAllCollapsedNew });
        setCollapsedGroups(newState);
    };

    // grouping per convo_id
    const groupProps = {
        onRenderHeader: onRenderGroupHeader,
        onRenderGroup: onRenderGroup,
        onToggleCollapseAll: onToggleCollapseAll
    };

    // expand/collapse all groups
    const expandAllGroups = () => {
        const newState: { [key: string]: boolean } = {};
        groupedChats.groups.forEach(g => { newState[g.key] = false });
        setCollapsedGroups(newState);
    };

    const collapseAllGroups = () => {
        const newState: { [key: string]: boolean } = {};
        groupedChats.groups.forEach(g => { newState[g.key] = true });
        setCollapsedGroups(newState);
    };

    const filterToConversation = (group: IGroup) => {
        setFocusedConvoGroupKey(group.key);
        // auto-expand the focused group
        setCollapsedGroups(prev => ({ ...prev, [group.key]: false }));
    };

    const clearConvoFilter = () => {
        setFocusedConvoGroupKey(null);
    };

    const handleLinkClick = (item: any) => {
        setSelectedKey(item.props.itemKey);
    };

    return (
        <div className={styles.container}>
            {spaceChatsLoaded ? (
                <div className={styles.timeFilterInputsRoot}>
                    {/* filter controls always visible above tabs */}
                    <div className={styles.timeFilterInputsContainer}>
                        <div className={styles.timeDropsGroup}>
                            <TextField
                                styles={textFieldStylesAnalyticsConvoList}
                                label="Start Date"
                                type="date"
                                value={startDate}
                                onChange={(_, date) => {
                                    setStartDate(date || '');
                                    setActivePeriod(null);
                                }}
                                className={styles.fCo}
                            />
                            <TextField
                                styles={textFieldStylesAnalyticsConvoList}
                                label="End Date"
                                type="date"
                                value={endDate}
                                onChange={(_, date) => {
                                    setEndDate(date || '');
                                    setActivePeriod(null);
                                }}
                                className={styles.fCo}
                            />
                            <Dropdown
                                label="Period"
                                placeholder="Select period"
                                selectedKey={activePeriod}
                                onChange={(_, option) => {
                                    if (option) setPredefinedPeriod(option.key as string)
                                }}
                                options={reportingPeriodsDropdownOptions}
                                className={styles.fCo}
                                styles={dropdownAnalyticsPeriod}
                            />
                            {selectedKey == "ChatAnalytics" && (
                                <Dropdown
                                    label="User"
                                    selectedKey={userMail}
                                    onChange={(_, item) => setUserMail(item?.key)}
                                    options={dropdownOptions}
                                    className={styles.fCo}
                                    styles={dropdownFileSrcStylesAnalyticsUsers}
                                />
                            )}

                            <div className={styles.controlButGroup}>
                                {focusedConvoGroupKey && (
                                    <div className={styles.convoFilterPillWrap}>
                                        <div className={styles.convoFilterPill} onClick={clearConvoFilter} title="Clear conversation filter">
                                            <span>Convo: {focusedConvoGroupKey}</span>
                                            <IoMdCloseCircleOutline size={16} />
                                        </div>
                                    </div>
                                )}
                                {selectedKey != "SyncLogAnalytics" && fltrdChats.length > 0 && (
                                    <div className={styles.expandCollapseButtonGroup}>
                                        <div className={styles.expandCollapseBtn} onClick={expandAllGroups}>
                                            <BiExpandVertical size="20" />
                                        </div>
                                        <div className={styles.expandCollapseBtn} onClick={collapseAllGroups}>
                                            <BiCollapseVertical size="20" />
                                        </div>
                                    </div>
                                )}
                                {selectedKey != "SyncLogAnalytics" && fltrdChats.length > 0 && (
                                    <div className={styles.downloadChatLogCsvIcon} onClick={() => downloadCSV(fltrdChats)}>
                                        <PiFileCsv size="25" />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    {/* tabs for analytics */}
                    <Pivot onLinkClick={handleLinkClick}  styles={libPivotStyles} selectedKey={selectedKey}>
                        {/* chat analytics */}
                        <PivotItem itemKey="ChatAnalytics" headerText="Chat Analytics" >
                            {/* chat tab */}
                            <Stack tokens={{ childrenGap: 20 }} className={styles.fckstak} styles={stackStylesAnalytics}>
                                {/* chart & cards */}
                                {selectedKey != "SyncLogAnalytics" && fltrdChats.length > 0 ? (
                                    <Stack.Item className={styles.chartContainer}>
                                        <div style={{ width: "100vw" }}>
                                            {fltrdChats.length > 0 ? (
                                                <div className={styles.chartBar}>
                                                    <Stack horizontal horizontalAlign={"stretch"} wrap tokens={{ childrenGap: 2 }}  className={styles.fckstak} styles={stackStylesAnalytics}>
                                                        {/* chart */}
                                                        <Stack.Item className={styles.convoOverviewContainerOne}>
                                                            <ChartAnalyticsBar data={fltrdChats.map(item => ({ ...item, key: item.key.toString().substring(0, 8) }))} onFilterChange={handleChatUserFilterChange} />
                                                        </Stack.Item>
                                                        {/* cards */}
                                                        <Stack.Item className={styles.convoOverviewContainerTwo}>
                                                            <div className={styles.convoOverviewPanel}>
                                                                {groupedChats.groups.map(group => (
                                                                    <div
                                                                        key={group.key}
                                                                        className={`${
                                                                            group.data.numericFeedbackSum && group.data.numericFeedbackSum != 0
                                                                                ? group.data.numericFeedbackSum > 0
                                                                                    ? styles.positiveFeedback
                                                                                    : styles.negativeFeedback
                                                                                : styles.collectedDataCard
                                                                        } ${focusedConvoGroupKey === group.key ? styles.cardSelected : ""}`}
                                                                    >
                                                                        <div className={styles.titleandbutscard}>
                                                                            <div className={styles.collectedDataCardHeader}>{group.name}</div>
                                                                            <div className={styles.cardButtonsGroup}>
                                                                                <div
                                                                                    className={`${styles.scrollToConvoBut} ${focusedConvoGroupKey === group.key ? styles.scrollToConvoButActive : ""}`}
                                                                                    onClick={() => focusedConvoGroupKey === group.key ? clearConvoFilter() : filterToConversation(group)}
                                                                                    title={focusedConvoGroupKey === group.key ? "Clear filter" : "Filter to this conversation"}
                                                                                >
                                                                                    <TbFilterStar />
                                                                                </div>
                                                                                {group.data.audio_logs && group.data.audio_logs.length > 0 ? (
                                                                                    <div className={styles.downloadaudiobut} onClick={() => downloadAudio(group.data.audio_logs?.[0] || [])}>
                                                                                        <HiOutlineDocumentDownload />
                                                                                    </div>
                                                                                ) : (
                                                                                    <div className={styles.downloadaudiobutdisabled} onClick={() => downloadAudio(group.data.audio_logs?.[0] || [])}>
                                                                                        {" "}
                                                                                        <HiOutlineDocumentDownload />
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                        <CollectedDataTable group={group} assistantType={selectedGroup.assistanttype} />
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </Stack.Item>
                                                    </Stack>
                                                </div>
                                            ) : (
                                                <div className={styles.noDataMessage}>No Data Available for Selected Filters</div>
                                            )}
                                        </div>
                                    </Stack.Item>
                                ) : (
                                    <div className={styles.noDataMessage}>No Chat Log Data Available.</div>
                                )}

                                {/* sync log table */}
                                {selectedKey != "SyncLogAnalytics" && fltrdChats.length > 0 && (
                                    <Stack.Item className={styles.tableAWrap}>
                                        {/* table */}
                                        {fltrdChats.length > 0 && (
                                            <ThemeProvider theme={detailsListTheme}>
                                                <div>
                                                    <DetailsList
                                                        className={styles.detailsList}
                                                        styles={controlStylesAnalyticsConvoList}
                                                        items={displayChats.chats}
                                                        columns={columns}
                                                        setKey="set"
                                                        layoutMode={DetailsListLayoutMode.justified}
                                                        selectionPreservedOnEmptyClick={true}
                                                        ariaLabelForSelectionColumn="Toggle selection"
                                                        ariaLabelForSelectAllCheckbox="Toggle selection for all items"
                                                        selectionMode={0}
                                                        onItemInvoked={item => {
                                                            setChosenItem(item);
                                                            setIsItemDetailsOpen(true);
                                                        }}
                                                        onRenderRow={(prps: IDetailsRowProps | undefined): JSX.Element => {
                                                            // find group for this row
                                                            const group = displayChats.groups.find(g => prps && prps.item && String(prps.item.convo_id) === g.key);
                                                            const isCollapsed = group ? collapsedGroups[group.key] : false;
                                                            return (
                                                                <div className={styles.groupRow + (isCollapsed ? " " + styles.groupRowCollapsed : "")}>
                                                                    <DetailsRow {...(prps as IDetailsRowProps)} styles={prps?.styles} />
                                                                </div>
                                                            );
                                                        }}
                                                        onShouldVirtualize={() => displayChats.chats.length > 10}
                                                        groups={displayChats.groups.map(g => ({
                                                            ...g,
                                                            isCollapsed: collapsedGroups[g.key] || false
                                                        }))}
                                                        groupProps={groupProps}
                                                    />
                                                </div>
                                            </ThemeProvider>
                                        )}
                                    </Stack.Item>
                                )}
                            </Stack>
                        </PivotItem>

                        {/* 📒 sync tab 📒 */}
                        <PivotItem itemKey="SyncLogAnalytics" headerText="Processing Log">
                            <div style={{ width: "100vw" }}>
                                {/* chart */}
                                <div className={styles.chartBar}>
                                    <ChartSyncLogBar data={fltrdSyncs} onFilterChange={handleChartFilterChange} />
                                </div>
                                {/* table */}
                                <TableSyncLog data={tableSyncs} />
                            </div>
                        </PivotItem>
                    </Pivot>

                    <Modal
                        isOpen={isItemDetailsOpen}
                        onDismiss={() => {
                            setIsItemDetailsOpen(false);
                        }}
                        isBlocking={false}
                        containerClassName={styles.spaceAnalyticsModal}
                    >
                        <div className={styles.modalContent}>
                            <div className={styles.modalHeader}>
                                <h2>Chat Details</h2>
                            </div>
                            <div className={styles.modalBody}>
                                <table className={styles.detailsTable}>
                                    <tbody>
                                        <tr>
                                            <th>Date</th>
                                            <td>{chosenItem?.key}</td>
                                        </tr>
                                        <tr>
                                            <th>User</th>
                                            <td>{chosenItem?.usermail}</td>
                                        </tr>
                                        <tr>
                                            <th>Query</th>
                                            <td>{chosenItem?.question}</td>
                                        </tr>
                                        <tr>
                                            <th>Response</th>
                                            <td>
                                                <div className={styles.detailsResponse}>
                                                    {formatText(chosenItem ? chosenItem?.response.replace(/"/g, "") : "")}
                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div className={styles.modalFooter}>
                                <button
                                    className={styles.closeButton}
                                    onClick={() => {
                                        setIsItemDetailsOpen(false);
                                    }}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </Modal>
                </div>
            ) : (
                <div className={styles.loadingIndicatorCenterScreen}>
                    <div className={styles.typingDots}>
                        <span></span>
                        <span></span>
                        <span></span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AnalyticsConvoList;
